import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Card } from 'antd';
import { connect } from 'react-redux';
import IntlMessages from 'util/IntlMessages';
import { onFetchGmapData } from '@uhe_actions/monitoring/UHEActions';
import { MAP_FILTER_PREFIX } from '@constants/UHESettings';
import PropTypes, { bool, func } from 'prop-types';
import UHEMarkerClusterer from './Components/UHEMarkerClusterer';

/**
 * Renders Map Clustering
 */
class MapClustering extends Component {
  /**
   * Part of React lifecycle
   * @param  {object} nextProps Next Props
   * @param {object} prevState Prev State
   * @return {void}
   */
  static getDerivedStateFromProps(nextProps, prevState) {
    const { filter } = nextProps;
    if (JSON.stringify(filter) === JSON.stringify(prevState.filter)) {
      return null;
    }
    nextProps.onFetchGmapData(filter);
    return { filter };
  }

  /**
 * The default function for determining the label text and style
 * for a cluster icon.
 *
 * @param {Array.<google.maps.Marker>} markers The array of markers represented by the cluster.
 * @param {number} numStyles The number of marker styles available.
 * @returns {ClusterIconInfo} The information resource for the cluster.
 */
  static clusterRenderer(markers, numStyles) {
    const title = '';
    const count = markers.length.toString();
    let index = 0;
    let dv = count;

    while (dv !== 0) {
      dv = parseInt(dv / 10, 10);
      index += 1;
    }

    index = Math.min(index, numStyles);
    let finalIndex;

    for (let i = 0; i < markers.length; i += 1) {
      if (markers[i].icon === '/marker_unhealthy.png') {
        if (index === 1) finalIndex = 3;
        if (index === 2) finalIndex = 6;
        if (index === 3) finalIndex = 9;
        break;
      } else if (markers[i].icon === '/marker_not_configured.png') {
        if (index === 1) finalIndex = 2;
        if (index === 2) finalIndex = 5;
        if (index === 3) finalIndex = 8;
      }
    }

    if (!finalIndex) {
      if (index === 1) finalIndex = 1;
      if (index === 2) finalIndex = 4;
      if (index === 3) finalIndex = 7;
    }
    return {
      text: count,
      index: finalIndex,
      title,
    };
  }

  /**
   * MapClustering Constructor
   * @param {object} props MapClustering Constructor Props
   * @return {void}
   */
  constructor(props) {
    super(props);

    this.state = {};

    const { filter } = props;
    props.onFetchGmapData(filter);

    this.history = props.history;
    this.currentZoom = null;
    this.currentBounds = null;
    this.currentCenter = null;
    this.debounceTimeout = null;
    this.qParams = new URLSearchParams(props.history.location.search);
    this.getFilterParameters();

    this.mapOptions = {
      controlSize: 25,
      fullscreenControl: false,
      streetViewControl: false,
    };

    this.clusterStyles = [{
      url: '/small_healthy.png',
      width: 53,
      height: 53,
      anchorIcon: [26, 15],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/small_not_configured.png',
      width: 53,
      height: 53,
      anchorIcon: [26, 15],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/small_unhealthy.png',
      width: 53,
      height: 53,
      anchorIcon: [26, 15],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/medium_healthy.png',
      width: 56,
      height: 56,
      anchorIcon: [35, 20],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/medium_not_configured.png',
      width: 56,
      height: 56,
      anchorIcon: [35, 20],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/medium_unhealthy.png',
      width: 56,
      height: 56,
      anchorIcon: [35, 20],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/large_healthy.png',
      width: 66,
      height: 66,
      anchorIcon: [44, 25],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/large_not_configured.png',
      width: 66,
      height: 66,
      anchorIcon: [44, 25],
      anchorText: [0, 0],
      textSize: 11,
    },
    {
      url: '/large_unhealthy.png',
      width: 66,
      height: 66,
      anchorIcon: [44, 25],
      anchorText: [0, 0],
      textSize: 11,
    },
    ];

    this.onZoomBoundsChanged = this.onZoomBoundsChanged.bind(this);
    this.renderResetButton = this.renderResetButton.bind(this);
    this.clearMapFilters = this.clearMapFilters.bind(this);
  }

  /**
   * Handles map zoom and boundaries change event
   * @param {integer} zoom map zoom
   * @param {object} bounds map bounds
   * @param {object} center map center
   * @return {void}
   */
  onZoomBoundsChanged(zoom, bounds, center) {
    if (zoom && bounds && this.hasZoomBoundsChanged(zoom, bounds)) {
      this.currentZoom = zoom;
      this.currentBounds = bounds.toJSON();
      this.currentCenter = center;
      this.debounceAction(this.setFilterParameters, 2000);
    }
  }

  /**
   * Sets URL parameters for map boundary
   * @return {void}
   */
  setFilterParameters() {
    this.qParams.set(`${MAP_FILTER_PREFIX}zoom`, this.currentZoom);
    this.qParams.set(`${MAP_FILTER_PREFIX}center_latitude`, this.currentCenter.lat());
    this.qParams.set(`${MAP_FILTER_PREFIX}center_longitude`, this.currentCenter.lng());
    this.qParams.set(`${MAP_FILTER_PREFIX}latitude_min`, this.currentBounds.south);
    this.qParams.set(`${MAP_FILTER_PREFIX}latitude_max`, this.currentBounds.north);
    this.qParams.set(`${MAP_FILTER_PREFIX}longitude_min`, this.currentBounds.west);
    this.qParams.set(`${MAP_FILTER_PREFIX}longitude_max`, this.currentBounds.east);

    this.history.push({ search: this.qParams.toString() });
  }

  /**
   * Gets map boundary values from URL
   * @return {void}
   */
  getFilterParameters() {
    const radixParam = 10;
    if (this.qParams.get(`${MAP_FILTER_PREFIX}zoom`)) {
      this.currentZoom = parseInt(this.qParams.get(`${MAP_FILTER_PREFIX}zoom`), radixParam);
    }

    if (this.qParams.get(`${MAP_FILTER_PREFIX}center_latitude`) && this.qParams.get(`${MAP_FILTER_PREFIX}center_longitude`)) {
      this.currentCenter = {
        lat: this.qParams.get(`${MAP_FILTER_PREFIX}center_latitude`) ? parseFloat(this.qParams.get(`${MAP_FILTER_PREFIX}center_latitude`)) : null,
        lng: this.qParams.get(`${MAP_FILTER_PREFIX}center_longitude`) ? parseFloat(this.qParams.get(`${MAP_FILTER_PREFIX}center_longitude`)) : null,
      };
    }

    if (this.qParams.get(`${MAP_FILTER_PREFIX}latitude_min`) && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_max`)
        && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_min`) && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_max`)) {
      this.currentBounds = {
        south: parseFloat(this.qParams.get(`${MAP_FILTER_PREFIX}latitude_min`)),
        north: parseFloat(this.qParams.get(`${MAP_FILTER_PREFIX}latitude_max`)),
        west: parseFloat(this.qParams.get(`${MAP_FILTER_PREFIX}longitude_min`)),
        east: parseFloat(this.qParams.get(`${MAP_FILTER_PREFIX}longitude_max`)),
      };
    }
  }

  /**
   * Detects zoom or map boundary change
   * @param {integer} zoom zoom
   * @param {object} bounds bounds
   * @return {Boolean} true|false
   */
  hasZoomBoundsChanged(zoom, bounds) {
    const zoomChanged = zoom !== this.currentZoom;
    const boundsChanged = bounds
      ? JSON.stringify(this.currentBounds) !== JSON.stringify(bounds.toJSON())
      : false;

    return zoomChanged || boundsChanged;
  }

  /**
   * Debounces a callback with a defined delay
   * @param  {...any} args an Array-like object accessible inside functions
   * that contains the values of the arguments passed to that function.
   * @return {void}
   */
  debounceAction(...args) {
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => args[0].apply(this, args), args[1]);
  }

  /**
   * Clears map zoom and boundary parameters from URL
   *
   * @returns {void}
   */
  clearMapFilters() {
    let sholdPush = false;

    if (this.qParams.get(`${MAP_FILTER_PREFIX}zoom`)) {
      this.qParams.delete(`${MAP_FILTER_PREFIX}zoom`);
      sholdPush = true;
    }

    if (this.qParams.get(`${MAP_FILTER_PREFIX}center_latitude`) && this.qParams.get(`${MAP_FILTER_PREFIX}center_longitude`)) {
      this.qParams.delete(`${MAP_FILTER_PREFIX}center_latitude`);
      this.qParams.delete(`${MAP_FILTER_PREFIX}center_longitude`);
      sholdPush = true;
    }

    if (this.qParams.get(`${MAP_FILTER_PREFIX}latitude_min`) && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_max`)
        && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_min`) && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_max`)) {
      this.qParams.delete(`${MAP_FILTER_PREFIX}longitude_min`);
      this.qParams.delete(`${MAP_FILTER_PREFIX}longitude_max`);
      this.qParams.delete(`${MAP_FILTER_PREFIX}latitude_min`);
      this.qParams.delete(`${MAP_FILTER_PREFIX}latitude_max`);
      sholdPush = true;
    }

    if (sholdPush) {
      this.history.push({ search: this.qParams.toString() });
    }
  }

  /**
   * Returns a boolean value if any map filte is currently active
   *
   * @return {Boolean} [description]
   */
  isMapFilterActive() {
    return this.qParams.get(`${MAP_FILTER_PREFIX}zoom`)
      || (this.qParams.get(`${MAP_FILTER_PREFIX}center_latitude`) && this.qParams.get(`${MAP_FILTER_PREFIX}center_longitude`))
      || (
        this.qParams.get(`${MAP_FILTER_PREFIX}latitude_min`) && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_max`)
          && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_min`) && this.qParams.get(`${MAP_FILTER_PREFIX}latitude_max`)
      );
  }

  /**
   * Renders the reset button element
   * @return {JSX} React Element
   */
  renderResetButton() {
    const { showResetButton } = this.props;
    const statusFilter = this.isMapFilterActive();

    if (showResetButton) {
      return (
        <button className="ant-btn reset-btn ant-btn-sm" onClick={this.clearMapFilters} disabled={!statusFilter} type="button">
          <IntlMessages id="uhe.map.clearFilter" />
        </button>
      );
    }

    return '';
  }

  /**
   * Renders the component
   * @return {JSX} React Element
   */
  render() {
    const { loading } = this.props;
    return (
      <Card id="map-filter-holder" loading={loading} className="gx-card" title={<IntlMessages id="uhe.map.title" />} extra={this.renderResetButton()}>
        <UHEMarkerClusterer
          onZoomBoundsChanged={this.onZoomBoundsChanged}
          zoom={this.currentZoom}
          mapCenter={this.currentCenter}
          mapOptions={this.mapOptions}
          clusterStyles={this.clusterStyles}
          clusterRenderer={MapClustering.clusterRenderer}
        />
      </Card>
    );
  }
}

/**
 * Selects the part of the data from the store that the connected component needs.
 * @param {object} UheGmap Uhe Gmap
 * @return {object} data - return an object of data this component needs
 */
const mapStateToProps = ({ UheGmap = {} }) => {
  const { loading = true, data = [] } = UheGmap;

  return { loading, data };
};

MapClustering.defaultProps = {
  filter: [],
  loading: bool,
  onFetchGmapData: func,
  history: {},
  showResetButton: true,
};

MapClustering.propTypes = {
  filter: PropTypes.arrayOf(
    PropTypes.shape,
  ),
  loading: PropTypes.bool,
  onFetchGmapData: PropTypes.func,
  history: PropTypes.shape({
    length: PropTypes.number,
    action: PropTypes.string,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      hash: PropTypes.string,
      key: PropTypes.string,
    }),
  }),
  showResetButton: PropTypes.bool,
};

export default connect(mapStateToProps, {
  onFetchGmapData,
})(withRouter(MapClustering));
